import { useEffect, useState, useMemo, useCallback } from "react"

export const useUnload = (isDirty: boolean) => {
    useEffect(() => {
        const onUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }

        if (isDirty) {
            window.addEventListener('beforeunload', onUnload)
        }
        return () => {
            window.removeEventListener('beforeunload', onUnload)
        }
    }, [isDirty])
}

export const useModal = (): [boolean, () => void, () => void] => {
  const [modalVisible, setModalOpen] = useState(false)

  const showModal = () => setModalOpen(true)
  const hideModal = () => setModalOpen(false)

  return [modalVisible, showModal, hideModal]
}

const usePagination = (initialPagination = {current: 1, pageSize: 10}) => useState<{current?: number, pageSize?: number}>(initialPagination);

export const useQueryVariables = (defaultFilters = {}, defaultSort = { column: 'createdAt', direction: 'DESC' }, defaultPagination = {current: 1, pageSize: 10}) => {
  const [pagination, setPaginationInternal] = usePagination(defaultPagination)
  const [filters, setFiltersInternal] = useState(defaultFilters)
  const [sort, setSortInternal] = useState(defaultSort)

  // Memoize setters to prevent unnecessary re-renders and stale closures
  const setPagination = useCallback((newPagination: {current?: number, pageSize?: number}) => {
    setPaginationInternal(newPagination)
  }, [])

  const setFilters = useCallback((newFilters: any) => {
    setFiltersInternal(newFilters)
  }, [])

  const setSort = useCallback((newSort: any) => {
    setSortInternal(newSort)
  }, [])

  const variables = useMemo(() => {
    const result = {
      filters,
      page: pagination.current,
      perPage: pagination.pageSize,
      sort
    }
    return result
  }, [filters, pagination, sort])

  return {variables, setPagination, setFilters, setSort}
}
